/**
 * Data Deletion — wavedsp.ai/data-deletion
 *
 * Public, unauthenticated. Required by:
 *   • Meta App Review (Instagram Business Login) — must provide a User
 *     Data Deletion callback URL OR clear instructions; reviewers visit
 *     THIS URL when grading the submission.
 *   • TikTok Developer Audit — Data Deletion URL is one of the three
 *     URLs the audit form requires (alongside Privacy + Terms).
 *   • GDPR Article 17 (Right to Erasure).
 *   • CCPA / CPRA "Do Not Sell or Delete My Data" surface.
 *
 * The page lists three deletion paths the reviewer can verify:
 *   1. Self-serve in-product Disconnect (the preferred path — works
 *      without contacting us).
 *   2. Public-facing deletion request form (email-based fallback).
 *   3. Meta-initiated deletion callback (backend POST endpoint at
 *      /api/legal/meta-data-deletion-callback — see backend/routes/
 *      legal.py).
 */
import React, { useEffect, useState } from 'react';
import LegalLayout from '../../components/legal/LegalLayout';

export default function DataDeletionPage() {
  const [submittedTicket, setSubmittedTicket] = useState('');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Data Deletion · WAVE DSP';
    return () => { document.title = prev; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      email: fd.get('email')?.toString().trim(),
      platform: fd.get('platform')?.toString().trim(),
      reason: fd.get('reason')?.toString().trim(),
    };
    if (!payload.email) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/data-deletion-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (j && j.ticket_id) {
        setSubmittedTicket(j.ticket_id);
        form.reset();
      } else {
        setSubmittedTicket('FALLBACK');
      }
    } catch (_err) {
      // Fail-soft — the email path is the always-available fallback.
      setSubmittedTicket('FALLBACK');
    }
  }

  return (
    <LegalLayout title="Data Deletion" lastUpdated="June 24, 2026">
      <p>
        You can delete your WAVE account &mdash; and all the personal data
        associated with it &mdash; at any time. This page describes three ways
        to do that, with timelines and what gets retained.
      </p>

      <h2>1. Self-serve (fastest &mdash; 1 minute)</h2>
      <p>If you have an active WAVE account:</p>
      <ul>
        <li>Sign in at <a href="/auth/login">wavedsp.ai/auth/login</a>.</li>
        <li>Open <em>Settings &rarr; Account &rarr; Delete account</em>.</li>
        <li>Confirm. Your account is closed immediately; personal data is
          purged within 30 days.</li>
      </ul>
      <p>
        If you only want to disconnect a single social platform (Instagram,
        TikTok, or YouTube) without closing your WAVE account, open{' '}
        <em>Settings &rarr; Connected Accounts &rarr; Disconnect</em>. Revocation
        is immediate &mdash; WAVE&rsquo;s read-only token is invalidated and
        we stop reading from that platform&rsquo;s API.
      </p>

      <h2>2. Public request (no account needed)</h2>
      <p>
        Use this form if you can&rsquo;t sign in &mdash; for example, you only
        connected a social account once and no longer have access to the email
        you used. We respond within 5 business days; full deletion completes
        within 30 days, in line with GDPR Article 12.
      </p>

      {submittedTicket ? (
        <div data-testid="deletion-ticket-confirm" style={{
          border: '1px solid #1F1F1F', borderRadius: 12,
          background: '#0F0F0F', padding: '18px 22px', margin: '12px 0 8px',
        }}>
          <p style={{ color: '#00D4FF', fontWeight: 700, marginBottom: 6 }}>
            Request received.
          </p>
          <p style={{ color: '#aaa', fontSize: '13.5px', lineHeight: 1.7 }}>
            {submittedTicket === 'FALLBACK' ? (
              <>We couldn&rsquo;t reach our server right now. Please email{' '}
                <a href="mailto:privacy@wavedsp.ai?subject=Data%20Deletion%20Request">
                  privacy@wavedsp.ai
                </a>{' '}
                directly &mdash; reference &ldquo;Data Deletion Request.&rdquo;
              </>
            ) : (
              <>Ticket <code>{submittedTicket}</code>. We&rsquo;ll email you a
                confirmation within 5 business days. If you don&rsquo;t hear back,
                forward your original request to{' '}
                <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a>.
              </>
            )}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-testid="deletion-request-form" style={{
          border: '1px solid #1F1F1F', borderRadius: 12,
          background: '#0F0F0F', padding: '20px 22px', margin: '10px 0 4px',
        }}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, display: 'block', marginBottom: 6 }}>
              Your email <span style={{ color: '#FF4D94' }}>*</span>
            </span>
            <input
              type="email" name="email" required
              data-testid="deletion-form-email"
              placeholder="you@example.com"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                background: '#0A0A0A', border: '1px solid #2A2A2A',
                color: '#fff', fontSize: 14,
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, display: 'block', marginBottom: 6 }}>
              Which platform did you connect?
            </span>
            <select
              name="platform"
              data-testid="deletion-form-platform"
              defaultValue=""
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                background: '#0A0A0A', border: '1px solid #2A2A2A',
                color: '#fff', fontSize: 14,
              }}
            >
              <option value="">Select&hellip;</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
              <option value="brand">I was a brand (not a creator)</option>
              <option value="unknown">I&rsquo;m not sure</option>
            </select>
          </label>
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, display: 'block', marginBottom: 6 }}>
              Anything we should know? (optional)
            </span>
            <textarea
              name="reason" rows="3"
              data-testid="deletion-form-reason"
              placeholder="e.g., I lost access to the email I signed up with."
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                background: '#0A0A0A', border: '1px solid #2A2A2A',
                color: '#fff', fontSize: 14, resize: 'vertical',
              }}
            />
          </label>
          <button
            type="submit"
            data-testid="deletion-form-submit"
            style={{
              background: '#FF4D94', color: '#fff',
              padding: '10px 18px', borderRadius: 999,
              border: 'none', fontWeight: 800, fontSize: 13,
              letterSpacing: '0.02em', cursor: 'pointer',
            }}
          >
            Submit deletion request
          </button>
        </form>
      )}

      <h2>3. Meta-initiated deletion callback</h2>
      <p>
        If you remove WAVE from your Facebook or Instagram account&rsquo;s
        Connected Apps screen, Meta automatically sends a deletion-callback
        request to our server at:
      </p>
      <p>
        <code>POST https://wavedsp.ai/api/legal/meta-data-deletion-callback</code>
      </p>
      <p>
        We verify the <code>signed_request</code> payload, identify your WAVE
        account via your Facebook user ID, and queue full deletion. You receive
        a confirmation URL and a ticket reference from Meta directly; you can
        check the ticket status at:
      </p>
      <p>
        <code>https://wavedsp.ai/data-deletion/status?id=&lt;ticket_id&gt;</code>
      </p>

      <h2>What gets deleted</h2>
      <ul>
        <li>Your account record (email, name, profile data).</li>
        <li>OAuth refresh tokens for any connected social account.</li>
        <li>Personal data in waves, briefs, drafts, and submitted post URLs.</li>
        <li>Notification history and in-app messages.</li>
        <li>Payment metadata stored on our side. (Stripe&rsquo;s independent
          records are retained per Stripe&rsquo;s own policy and U.S. tax law.)</li>
      </ul>

      <h2>What we have to retain</h2>
      <p>
        We are legally required to keep the following for a limited period
        after deletion:
      </p>
      <ul>
        <li><strong>Tax records</strong> &mdash; for 7 years if you were a U.S.
          creator who received earnings via Stripe Connect (1099-NEC).</li>
        <li><strong>Anti-fraud signals</strong> &mdash; hashed identifiers
          (no readable PII) for up to 12 months to prevent ban-evasion.</li>
        <li><strong>Aggregated, de-identified wave performance metrics</strong>
          &mdash; e.g., &ldquo;Wave X reached 124K accounts.&rdquo; These do
          not identify you individually and are not personal data after
          aggregation.</li>
      </ul>
      <p>
        All retained data is encrypted at rest and access is restricted to
        compliance personnel under audit log.
      </p>

      <h2>Timeline</h2>
      <ul>
        <li><strong>Day 0</strong> &mdash; You submit a deletion request (any path).</li>
        <li><strong>Day 0&ndash;1</strong> &mdash; Account is immediately disabled;
          you can no longer sign in; all social-platform OAuth tokens are revoked.</li>
        <li><strong>Day 1&ndash;30</strong> &mdash; Personal data is purged from
          our primary databases. Aggregated, de-identified metrics are retained.</li>
        <li><strong>Day 30&ndash;90</strong> &mdash; Encrypted backups rotated out
          of cold storage.</li>
        <li><strong>Day 90+</strong> &mdash; Only legally-required tax records
          and hashed anti-fraud signals remain.</li>
      </ul>

      <h2>Contact</h2>
      <p>
        Questions about a deletion request, or need to escalate?{' '}
        <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a>.
        We aim to respond within 5 business days.
      </p>
    </LegalLayout>
  );
}
